import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
import type { Broadcast } from "@tdelivr/api-client/generated/notifications/notification-orchestrator.js";

import { audienceCell, badgeFor, broadcastDetail, messageCell, tilesFor } from "../broadcasts.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Driver broadcasts: messages to couriers on shift, by zone or group.
 *
 * Built to `19 WP18 Dispatch Operations.dc.html`, page `broadcasts`.
 *
 * **This page did not exist until 22 October, and the design's own citation is why.** It names
 * NTF-04 — the push adapter, whose rows are one message per device — for a board that draws one
 * message to an audience. The subject was modelled in the orchestrator instead, where an audience
 * belongs, and this is the board of it.
 *
 * **Its primary action is the first on this console that is actually offered.** Every other one
 * here is drawn and refused, because the design's form collects fields the operation does not take:
 * a phone booking is a ride request, a quote, a dispatch and then a trip, and nothing starts that
 * chain from an address. This one lines up exactly — `Zones · Service · English · Swahili · Send`
 * against an audience of zones and service classes, a body by locale and an optional time. Nothing
 * is invented and nothing designed is left out.
 *
 * **What the board cannot yet show, and says so.** The fan-out is not built: resolving an audience
 * means joining DRV-05 presence to GEO-06 areas, and the design's note — *"broadcasts never
 * interrupt a driver mid-trip; they arrive after drop-off"* — is a delivery rule that needs trip
 * state. So every count is absent rather than zero, because a broadcast that has not gone out has
 * not reached nobody.
 */
export interface BroadcastsProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. */
  readonly crumbs?: readonly string[];
  /** What this operator holds, which decides whether the form sends or explains. */
  readonly scopes?: ReadonlySet<string>;
}

const SEND_SCOPE = "broadcast:send";
const notifications = services.notifications__notification_orchestrator;

/** The design's two chip rows, in its own order. */
const ZONES = ["Westlands", "CBD", "JKIA", "Karen"] as const;
const CLASSES = ["Economy", "XL", "Boda"] as const;

export function Broadcasts({ transport, tenantId, crumbs, scopes }: BroadcastsProps): ReactNode {
  const [broadcasts, setBroadcasts] = useState<readonly Broadcast[] | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const [formOpen, setFormOpen] = useState(false);
  const [zones, setZones] = useState<readonly string[]>([]);
  const [classes, setClasses] = useState<readonly string[]>([]);
  const [english, setEnglish] = useState("");
  const [swahili, setSwahili] = useState("");
  const [busy, setBusy] = useState(false);
  const [formFailure, setFormFailure] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    const client = notifications.createNotificationOrchestratorClient(transport);
    try {
      const answered = await client.listBroadcasts();
      setBroadcasts(answered.broadcasts);
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["broadcast:read"]));
      setBroadcasts(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const tiles = useMemo(() => tilesFor(broadcasts ?? []), [broadcasts]);
  const selected = useMemo(
    () => (broadcasts ?? []).find((broadcast) => broadcast.broadcastId === selectedId),
    [broadcasts, selectedId],
  );

  /*
   * `undefined` scopes is a caller that does not know, drawn as able to act. Hiding a control from
   * somebody who holds the scope is how a permission question becomes a bug report about a missing
   * feature.
   */
  const viewOnly = scopes !== undefined && !scopes.has(SEND_SCOPE);

  const send = (): void => {
    if (english.trim().length === 0) return;
    setBusy(true);
    setFormFailure(undefined);
    const client = notifications.createNotificationOrchestratorClient(transport);
    client
      .sendBroadcast(
        {
          /*
           * Swahili is sent only when written. An empty locale is refused by the service rather
           * than delivered blank, and omitting it is how an operator says "I did not write it" —
           * which is a different act from leaving the box empty by accident.
           */
          body:
            swahili.trim().length === 0
              ? { en: english.trim() }
              : { en: english.trim(), sw: swahili.trim() },
          audience: { zones: [...zones], serviceClasses: [...classes] },
          scheduledFor: null,
        },
        {
          /*
           * Derived from the message and its audience, so pressing Send twice on the same
           * broadcast replays rather than sending it twice — which on this screen means the same
           * message arriving twice on hundreds of phones.
           */
          idempotencyKey:
            `broadcast/${tenantId}/${english.trim()}/${[...zones].sort().join(",")}`.slice(0, 200),
        },
      )
      .then(() => {
        setFormOpen(false);
        setEnglish("");
        setSwahili("");
        setZones([]);
        setClasses([]);
        return load();
      })
      .catch((error: unknown) => {
        setFormFailure(error instanceof Error ? error.message : "NTF-01 refused the broadcast.");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const toggle = (
    value: string,
    held: readonly string[],
    set: (next: readonly string[]) => void,
  ): void => {
    set(held.includes(value) ? held.filter((item) => item !== value) : [...held, value]);
  };

  /*
   * Handed to `StateView` with the grid as its children, which is how every other board on this
   * console is built. This page had an early return on `state !== undefined` instead, and that is
   * not the same thing: `stateForList` answers a state for the ready case too, so the condition
   * held on every render and the table, the tiles and the form below it never drew at all.
   */
  const state = failure ?? stateForList(broadcasts, {});

  /*
   * Hoisted out of the children so the loading state can draw it over the shimmer, which is what
   * the design does and what the other boards on this console already do.
   *
   * This board had no skeleton after Q-287 built one for thirty-three of them, and the reason it
   * was missed is the same wrong vocabulary this screen has now shed. The sweep that finds a board
   * matches a class attribute holding exactly one name ending `__head`; this board's head carried
   * two names, the borrowed `records__head` beside `broadcasts__row`, so it never entered the list
   * and nothing reported it absent. Renaming the classes made it visible and the check failed the
   * same minute.
   */
  const head = (
    <div className="broadcasts__head">
      <span>Message</span>
      <span>Audience</span>
      <span>Sent</span>
      <span className="broadcasts__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      <section className="page broadcasts">
        <PageHeading
          crumbs={crumbs ?? []}
          /*
           * NTF-01, where the design says NTF-04. Naming the service a screen actually reads is the
           * rule this console keeps — the push adapter holds deliveries, not broadcasts.
           */
          source="NTF-01"
          title="Driver broadcasts"
          description="Messages to drivers on shift, by zone or group."
          action={
            <button
              type="button"
              className="broadcasts__primary"
              disabled={viewOnly}
              title={
                viewOnly
                  ? `Needs ${SEND_SCOPE}. Your role can view only.`
                  : "Sends a message to every courier matching the audience you choose."
              }
              onClick={() => setFormOpen(true)}
            >
              <span aria-hidden="true" className="broadcasts__primary-glyph">
                campaign
              </span>
              New broadcast
            </button>
          }
        />

        <div className="broadcasts__tiles">
          <div className="broadcasts__tile">
            <span className="broadcasts__tile-label">Sent</span>
            <span className="broadcasts__tile-value">{tiles.sent}</span>
          </div>
          <div className="broadcasts__tile">
            <span className="broadcasts__tile-label">Scheduled</span>
            <span className="broadcasts__tile-value">{tiles.scheduled}</span>
          </div>
          <div className="broadcasts__tile">
            <span className="broadcasts__tile-label">Read rate</span>
            <span className="broadcasts__tile-value">
              {tiles.readRate === undefined ? (
                <span
                  className="broadcasts__tile-absent"
                  title="A read is a fact only for the in-app channel. NTF-05 models unread, read and dismissed; NTF-04's push status ends at delivered, so whether a courier looked at a push is not something this platform knows. 0% would say they all ignored it."
                >
                  —
                </span>
              ) : (
                `${(tiles.readRate * 100).toFixed(0)}%`
              )}
            </span>
          </div>
          <div className="broadcasts__tile">
            <span className="broadcasts__tile-label">Failed</span>
            <span className="broadcasts__tile-value">{tiles.failed}</span>
          </div>
        </div>

        {formOpen ? (
          <form
            className="broadcasts__form"
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            <p className="broadcasts__form-title">New driver broadcast</p>

            <fieldset className="broadcasts__chips">
              <legend>Zones</legend>
              {ZONES.map((zone) => (
                <button
                  key={zone}
                  type="button"
                  className={
                    zones.includes(zone)
                      ? "broadcasts__chip broadcasts__chip--on"
                      : "broadcasts__chip"
                  }
                  aria-pressed={zones.includes(zone)}
                  onClick={() => toggle(zone, zones, setZones)}
                >
                  {zone}
                </button>
              ))}
              <span className="broadcasts__chip-note">
                {zones.length === 0 ? "All zones" : `${String(zones.length)} selected`}
              </span>
            </fieldset>

            <fieldset className="broadcasts__chips">
              <legend>Service</legend>
              {CLASSES.map((serviceClass) => (
                <button
                  key={serviceClass}
                  type="button"
                  className={
                    classes.includes(serviceClass)
                      ? "broadcasts__chip broadcasts__chip--on"
                      : "broadcasts__chip"
                  }
                  aria-pressed={classes.includes(serviceClass)}
                  onClick={() => toggle(serviceClass, classes, setClasses)}
                >
                  {serviceClass}
                </button>
              ))}
              <span className="broadcasts__chip-note">
                {classes.length === 0 ? "All services" : `${String(classes.length)} selected`}
              </span>
            </fieldset>

            <label className="broadcasts__label" htmlFor="broadcast-en">
              English
            </label>
            <textarea
              id="broadcast-en"
              className="broadcasts__area"
              rows={2}
              value={english}
              disabled={busy}
              onChange={(event) => setEnglish(event.target.value)}
            />

            <label className="broadcasts__label" htmlFor="broadcast-sw">
              Swahili
            </label>
            <textarea
              id="broadcast-sw"
              className="broadcasts__area"
              rows={2}
              value={swahili}
              disabled={busy}
              onChange={(event) => setSwahili(event.target.value)}
            />

            {/*
              The design's `Send` select offers `Now | Schedule`, and only the first is drawn.
              NTF-01 takes a `scheduledFor` and would hold it, but nothing sends a scheduled
              broadcast when its time comes — there is no fan-out at all yet — so offering Schedule
              would be offering a message that never goes.
            */}
            <p
              className="broadcasts__absent"
              title="NTF-01 accepts a scheduled time and nothing sends it when it arrives: the fan-out is not built. Offering this would be offering a message that never goes out."
            >
              Send: now. Scheduling is not offered until a broadcast can actually be sent.
            </p>

            {formFailure === undefined ? null : (
              <p className="broadcasts__failure">{formFailure}</p>
            )}

            <div className="broadcasts__form-buttons">
              <button
                type="button"
                className="broadcasts__cancel"
                disabled={busy}
                onClick={() => {
                  setFormOpen(false);
                  setFormFailure(undefined);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="broadcasts__send"
                disabled={busy || english.trim().length === 0}
                title={
                  english.trim().length === 0
                    ? "NTF-01 requires an English body: it is what a courier is shown when their own locale is not one you wrote."
                    : "Sends to every courier matching this audience."
                }
              >
                {busy ? "Sending…" : "Send broadcast"}
              </button>
            </div>
          </form>
        ) : null}

        <StateView
          state={state}
          purpose="Broadcasts appear here as soon as one is sent to couriers on shift."
          onRetry={() => {
            void load();
          }}
          skeleton={{
            head,
            columns: 4,
            rowClassName: "broadcasts__row",
            // This board wraps its grid, and the wrapper is what puts it on a surface.
            wrapperClassName: "broadcasts__grid",
            // Both strings come from the trail the shell already draws, so the
            // sentence can never disagree with the breadcrumb above it.
            page: crumbs?.at(-1) ?? "this page",
            tenant: crumbs?.[0] ?? "this tenant",
          }}
        >
          <div className="broadcasts__grid">
            {head}

            {(broadcasts ?? []).length === 0 ? (
              <p className="broadcasts__empty">
                No broadcast has been sent. The first one you send appears here.
              </p>
            ) : (
              (broadcasts ?? []).map((broadcast) => {
                const badge = badgeFor(broadcast.state);
                const message = messageCell(broadcast);
                const audience = audienceCell(broadcast);
                return (
                  <div
                    key={broadcast.broadcastId}
                    data-selected={broadcast.broadcastId === selectedId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={broadcast.broadcastId === selectedId}
                    aria-label={`Open broadcast ${message.text}`}
                    onClick={() => setSelectedId(broadcast.broadcastId)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedId(broadcast.broadcastId);
                    }}
                    className="broadcasts__row"
                  >
                    <span className="broadcasts__lead" title={message.title}>
                      {message.text}
                    </span>
                    <span className="broadcasts__cell" title={audience.title}>
                      {audience.text}
                    </span>
                    <span className="broadcasts__cell">
                      {broadcast.scheduledFor === null
                        ? new Date(broadcast.createdAt).toISOString().slice(11, 16)
                        : new Date(broadcast.scheduledFor).toISOString().slice(11, 16)}
                    </span>
                    <span className="broadcasts__state">
                      <StateBadge
                        classKey={badge.classKey}
                        label={badge.label}
                        code="NTF-01"
                        status={broadcast.state}
                      />
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </StateView>

        <p className="broadcasts__note">
          <span aria-hidden="true" className="broadcasts__note-glyph">
            info
          </span>
          Broadcasts never interrupt a driver mid-trip; they arrive after drop-off.
        </p>
      </section>

      {selected === undefined ? null : (
        <RecordDetail
          record={broadcastDetail(selected)}
          open
          onClose={() => setSelectedId(undefined)}
        />
      )}
    </div>
  );
}
